import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { companyInfo } from '../mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/leads`, {
        ...formData,
        source: 'contact_page'
      });
      
      if (response.data) {
        toast.success('Quote request submitted! A pest professional will contact you shortly.');
        setFormData({ name: '', phone: '', email: '', service: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      toast.error('Something went wrong. Please call us directly at ' + companyInfo.phone);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-16" style={{ background: 'var(--bg-section)' }}>
        <div className="container text-center">
          <h1 className="heading-1 mb-6">Get Your Free Quote</h1>
          <p className="body-large max-w-3xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            Fast response, expert service, and eco-friendly solutions
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="heading-2 mb-6">Get in Touch</h2>
              <p className="body-medium mb-8" style={{ color: 'var(--text-secondary)' }}>
                Need immediate assistance? Call us now for same-day service, or fill out the form and a pest professional will contact you shortly.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                    <Phone size={24} style={{ color: 'var(--accent-text)' }} />
                  </div>
                  <div>
                    <h3 className="heading-3 mb-1">Phone</h3>
                    <a href={`tel:${companyInfo.phoneRaw}`} className="body-medium" style={{ color: 'var(--accent-text)' }}>
                      {companyInfo.phone}
                    </a>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Available 24/7 for emergencies</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                    <Mail size={24} style={{ color: 'var(--accent-text)' }} />
                  </div>
                  <div>
                    <h3 className="heading-3 mb-1">Email</h3>
                    <a href={`mailto:${companyInfo.email}`} className="body-medium" style={{ color: 'var(--accent-text)' }}>
                      {companyInfo.email}
                    </a>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>We respond within 2 hours</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                    <MapPin size={24} style={{ color: 'var(--accent-text)' }} />
                  </div>
                  <div>
                    <h3 className="heading-3 mb-1">Service Areas</h3>
                    <p className="body-medium">NYC, Long Island, Westchester</p>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>All 5 boroughs + surrounding counties</p>
                  </div>
                </div>
              </div>
              
              {/* Trust Badges */}
              <div className="mt-12 p-6 rounded-2xl" style={{ background: 'var(--accent-wash)', border: '1px solid var(--accent-primary)' }}>
                <h3 className="heading-3 mb-4">Why Choose Us?</h3>
                <ul className="space-y-2">
                  <li className="body-small">✓ 100% Eco-Friendly & Pet-Safe</li>
                  <li className="body-small">✓ Licensed & Insured</li>
                  <li className="body-small">✓ Same-Day Service Available</li>
                  <li className="body-small">✓ Satisfaction Guaranteed</li>
                </ul>
              </div>
            </div>

            {/* Quote Form */}
            <div className="product-card">
              <h2 className="heading-2 mb-6">Request Free Quote</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="body-small font-semibold block mb-2" style={{ color: 'var(--text-primary)' }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-accent-primary"
                    style={{ borderColor: 'var(--border-light)' }}
                    placeholder="John Smith"
                  />
                </div>
                
                <div>
                  <label className="body-small font-semibold block mb-2" style={{ color: 'var(--text-primary)' }}>
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-accent-primary"
                    style={{ borderColor: 'var(--border-light)' }}
                    placeholder="(555) 123-4567"
                  />
                </div>
                
                <div>
                  <label className="body-small font-semibold block mb-2" style={{ color: 'var(--text-primary)' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-accent-primary"
                    style={{ borderColor: 'var(--border-light)' }}
                    placeholder="john@example.com"
                  />
                </div>
                
                <div>
                  <label className="body-small font-semibold block mb-2" style={{ color: 'var(--text-primary)' }}>
                    Service Needed *
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-accent-primary"
                    style={{ borderColor: 'var(--border-light)' }}
                  >
                    <option value="">Select a service...</option>
                    <option value="inspection">Pest Inspection</option>
                    <option value="rats-mice">Rats & Mice</option>
                    <option value="bed-bugs">Bed Bugs</option>
                    <option value="cockroaches">Cockroaches</option>
                    <option value="ants">Ants</option>
                    <option value="fleas-mites">Fleas or Mites</option>
                    <option value="hornets-wasps">Hornets & Wasps</option>
                    <option value="spiders">Spiders</option>
                    <option value="flies">Flies</option>
                    <option value="bees">Bees</option>
                    <option value="bird-proofing">Bird Proofing</option>
                    <option value="mosquitoes">Mosquitoes</option>
                    <option value="termites">Termites</option>
                    <option value="wildlife">Wildlife Removal</option>
                    <option value="airbnb">Airbnb Pest Control</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="body-small font-semibold block mb-2" style={{ color: 'var(--text-primary)' }}>
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-accent-primary"
                    style={{ borderColor: 'var(--border-light)' }}
                    placeholder="Tell us about your pest problem..."
                  />
                </div>
                
                <button type="submit" className="btn-primary w-full" disabled={isSubmitting}>
                  <Send size={20} />
                  {isSubmitting ? 'Submitting...' : 'Request Free Quote'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency CTA */}
      <section className="py-16" style={{ background: 'var(--bg-section)' }}>
        <div className="container text-center">
          <h2 className="heading-2 mb-4">Need Help Right Now?</h2>
          <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
            Don't wait - call us for same-day emergency service
          </p>
          <a href={`tel:${companyInfo.phoneRaw}`} className="btn-primary text-lg px-8">
            <Phone size={20} />
            Emergency: {companyInfo.phone}
          </a>
        </div>
      </section>
    </div>
  );
};

export default Contact;